/*eslint no-magic-numbers: ["error", { "ignore": [0, 1, -1] }]*/
import React, { useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, Link } from 'react-router-dom'

import { showErrorToast, showSuccessToast } from 'utils/toast'
import 'components/Toast/index.css'
import Col from 'components/Col'
import Row from 'components/Row'
import Container from 'components/Container'
import validatorName from 'validators/name'
import validatorEmail from 'validators/email'
import validatorPassword from 'validators/password'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import Input from 'components/Input'
import Select from 'components/Select'
import PasswordInput from 'components/PasswordInput'
import kbaPartners from 'constants/kbaPartners'
import MultiSelectionDropdown from 'components/MultiSelectDropdown'
import { requestUserRegistration } from 'api/user'
import roles, { SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, RFP_ROLE_ID, BRC_ROLE_ID } from 'constants/roles'
import { CountriesContext } from 'providers/countries'
import CheckboxCheckedImage from 'assets/icons/checkbox-checked'
import CheckboxUnCheckedImage from 'assets/icons/checkbox-unchecked'

import { Title, StyledCheckBox, RegistrationFooter } from './styles'

const formattedKbaPartners = [...kbaPartners.map((item) => {
  return {
    value: item.id,
    label: item.name
  }
})]

const rolesAvailableForRegistration = roles.filter((role) => {
  return role.value !== SECRETARIAT_ROLE_ID && role.value !== BIRDLIFE_SECRETARIAT_ROLE_ID && role.value !== RFP_ROLE_ID && role.value !== BRC_ROLE_ID
})

const RegistrationPage = () => {
  const { countriesOptions } = useContext(CountriesContext)
  const history = useNavigate()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [countryCode, setCountryCode] = useState(null)
  const [countryOfInterest, setCountryOfInterest] = useState([])
  const [kbaPartner, setKbaPartner] = useState([])
  const [iucnMemberTextArea, setIucnMemberTextArea] = useState('')
  const [selectedRole, setSelectedRole] = useState(null)
  const [organisation, setOrganisation] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [error, setError] = useState({ field: '', error: '' })

  const formattedCountries = countriesOptions.map((item) => {
    return {
      label: item.label,
      value: item.code
    }
  })

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value)
  }

  const handleLastNameChange = (e) => {
    setLastName(e.target.value)
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
  }

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value)
  }

  const handleOrganisationChange = (e) => {
    setOrganisation(e.target.value)
  }

  const handleTermsAndConditionsChange = () => {
    setTermsAccepted((s) => {
      return !s
    })
  }

  const handleCountryCodeChange = (e) => {
    setCountryCode(e)
  }

  const handleRoleChange = (e) => {
    setSelectedRole(e)
  }

  const handleCountryofInterestChange = (e) => {
    setCountryOfInterest(e)
  }

  const handleKbaPartnerChange = (e) => {
    setKbaPartner(e)
  }

  const cleanForm = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setCountryCode(null)
    setCountryOfInterest([])
    setKbaPartner([])
    setIucnMemberTextArea('')
    setOrganisation('')
    setPassword('')
    setConfirmPassword('')
    setTermsAccepted(false)
    setError({ field: '', error: '' })
    setSelectedRole(null)
  }

  const isFormValid = () => {
    if (!validatorName(firstName)) {
      setError({ field: 'firstName', error: 'firstNameIsInvalid' })
      return false
    }

    if (!validatorName(lastName)) {
      setError({ field: 'lastName', error: 'lastNameIsInvalid' })
      return false
    }

    if (!validatorEmail(email)) {
      setError({ field: 'email', error: 'emailIsInvalid' })
      return false
    }
    const organisationMaxLength = 100
    if (organisation.length > organisationMaxLength) {
      setError({ field: 'organisation', error: 'organisationIsInvalid' })
      return false
    }

    if (!countryCode?.value) {
      setError({ field: 'country', error: 'countryCodeIsNotSelected' })
      return false
    }

    if (!selectedRole) {
      setError({ field: 'role', error: 'roleSelectIsMandatory' })
      return
    }

    if (countryOfInterest.length === 0) {
      setError({ field: 'countryOfInterest', error: 'interestSelectIsMandatory' })
      return false
    }

    if (!validatorPassword(password)) {
      setError({ field: 'password', error: 'passwordIsInvalid' })
      return false
    }

    if (password !== confirmPassword) {
      setError({ field: 'confirmPassword', error: 'confirmPasswordInvalid' })
      return false
    }

    if (!termsAccepted) {
      setError({ field: 'termsAndConditions', error: 'termsAndConditionsNotAccepted' })
      return false
    }

    setError({ field: '', error: '' })
    return true
  }

  function goToLoginPage() {
    history('/login')
  }

  const submitForm = async () => {
    if (!isFormValid()) {
      return
    }
    setIsSubmitting(true)

    try {
      const response = await requestUserRegistration({
        firstName,
        lastName,
        email,
        organisation,
        password,
        countryCode: countryCode.value,
        role: selectedRole.value,
        CountryOfInterestId: countryOfInterest,
        KbaPartnerId: kbaPartner,
        memberOfIUCNSpecialistGroup: iucnMemberTextArea
      })
      if (response.error) {
        setError(response)
      } else {
        cleanForm()
        showSuccessToast(<FormattedMessage id="successfulRegistration" />, 'showBorder')
        goToLoginPage()
      }
    } catch (e) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
    setIsSubmitting(false)
  }

  return (
    <Container>
      <Row>
        <Col xs={12}>
          <Title data-test="legend">
            <FormattedMessage id="registrationPage.pageTitle" />
          </Title>
        </Col>
      </Row>

      <Row>
        <Col md={4} mdOffset={2}>
          <Input
            field="firstName"
            value={firstName}
            label="firstNameLabel"
            onChange={handleFirstNameChange}
            placeholder="firstNamePlaceholder"
            error={error}
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
        <Col md={4}>
          <Input
            field="lastName"
            value={lastName}
            label="lastNameLabel"
            placeholder="lastNamePlaceholder"
            onChange={handleLastNameChange}
            error={error}
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} mdOffset={2}>
          <Input
            field="email"
            value={email}
            label="emailLabel"
            placeholder="emailPlaceholder"
            onChange={handleEmailChange}
            error={error}
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
        <Col md={4}>
          <Input
            field="organisation"
            value={organisation}
            label="organisationLabel"
            placeholder="organisationPlaceholder"
            onChange={handleOrganisationChange}
            error={error}
            dataTest='form-field'
            isOptional={true}
            marginBottomLabel="8px"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} mdOffset={2}>
          <Select
            field="country"
            value={countryCode}
            onChange={handleCountryCodeChange}
            options={formattedCountries}
            label="countryLabel"
            height="48px"
            error={error}
            placeholder="countryPlaceholder"
            menuPlacement="bottom"
            dataTest='form-field'
            marginBottomLabel="8px"
            isSearchable={true}
          />
        </Col>
        <Col md={4}>
          <Select
            field="role"
            value={selectedRole}
            onChange={handleRoleChange}
            options={rolesAvailableForRegistration}
            label="roleSelectLabel"
            height="48px"
            error={error}
            placeholder="roleSelectPlaceholder"
            menuPlacement="bottom"
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} mdOffset={2}>
          <MultiSelectionDropdown
            marginBottomLabel="8px"
            marginBottom="32px"
            label="countryOfInterestLabel"
            field="countryOfInterest"
            error={error}
            secondStepOptions={countriesOptions}
            value={countryOfInterest}
            saveChanges={handleCountryofInterestChange}
            infoTooltip="countryHint"
            isSearchable={true}
            selectAllOption={true}
          />
        </Col>
        <Col md={4} >
          <MultiSelectionDropdown
            marginBottomLabel="8px"
            marginBottom="32px"
            label="kbaPartnerLabel"
            field="kbaPartner"
            secondStepOptions={formattedKbaPartners}
            value={kbaPartner}
            saveChanges={handleKbaPartnerChange}
            infoTooltip="kbaPartnerHint"
            height="48px"
            isOptional={true}
          />
        </Col>
      </Row>
      <Row>
        <Col mdOffset={2} md={4}>
          <PasswordInput
            field="password"
            value={password}
            label="passwordLabel"
            placeholder="passwordPlaceholder"
            onChange={handlePasswordChange}
            error={error}
            hint="passwordHint"
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
        <Col md={4}>
          <PasswordInput
            field="confirmPassword"
            value={confirmPassword}
            label="confirmPasswordLabel"
            placeholder="confirmPasswordPlaceholder"
            onChange={handleConfirmPasswordChange}
            error={error}
            dataTest='form-field'
            marginBottomLabel="8px"
          />
        </Col>
      </Row>
      <Row>
        <Col dataTest="agreement-checkbox" mdOffset={2} md={4}>
          <StyledCheckBox>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div 
                  style={{ 
                    display: 'grid', 
                    placeItems: 'center', 
                    height: '24px', 
                    width: '24px', 
                    cursor: 'pointer',
                    marginRight: '8px'
                  }}
                  onClick={handleTermsAndConditionsChange}
                >
                  {termsAccepted ? (
                    <div style={{ 
                      height: '16px', 
                      width: '16px', 
                      display: 'grid', 
                      placeItems: 'center', 
                      borderRadius: '2px', 
                      background: '#0AA1F4'
                    }}>
                      <CheckboxCheckedImage />
                    </div>
                  ) : (
                    <CheckboxUnCheckedImage />
                  )}
                </div>
              </div>
              <span style={{ marginLeft: '8px' }}>
                <FormattedMessage id="termsAndConditionsText" />
                {' '}
                <Link
                  target="_blank"
                  data-test="agreement-terms-conditions"
                  style={{ color: '#202020' }}
                  exact
                  strict
                  to="/terms-and-use"
                >
                  Terms and Use
                </Link>
              </span>
            </div>
          </StyledCheckBox>
        </Col>
        <Col md={4}>
          <Button label="Submit" onClick={submitForm} isPending={isSubmitting} dataTest="create-account-btn" width='100%' type='primary'>
            <div>
              <FormattedMessage id="createAccountButton" />
            </div>
          </Button>
        </Col>
      </Row>
      <RegistrationFooter />
    </Container>
  )
}

export default RegistrationPage
